// The intake form, sent to the client by text.
//
// What this card is really for is answering "did they get it, and did
// they fill it in" without anybody having to ask the client. So the
// state it shows is the state of the form rather than the state of the
// message: sent, opened, submitted.
//
// Resending is deliberately plain rather than hidden behind a menu.
// The common case is a client who lost the text, and the person
// helping them is on the phone while they look.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

// No colour coding. "Sent" is not a warning and "submitted" is not a
// success state to celebrate — they are just where the form got to.
const STATE = {
  pending:   "Not sent yet",
  sent:      "Sent",
  opened:    "Opened by the client",
  submitted: "Completed",
  expired:   "Link expired",
  opted_out: "Client opted out of texts",
  revoked:   "Cancelled",
};

export function IntakeSmsCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState("");
  // Shown once, then it is gone. The token is not stored anywhere we
  // can read it back, so the person who asked for it has to copy it
  // now — minting another invalidates this one.
  const [link, setLink] = useState(null);
  // What the client said about providers, and what the app suggests.
  // Suggestions only — adding one goes through the ordinary provider
  // routes, so this card never writes to the matter itself.
  const [proposals, setProposals] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/matters/${matterId}/intake-sms`));
    } catch (e) {
      setData({ _error: e.message });
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const loadProposals = useCallback(async () => {
    try {
      setProposals(await apiFetch(`/api/matters/${matterId}/intake-proposals`));
    } catch (_e) {
      setProposals(null);   // Nothing to suggest is not an error state.
    }
  }, [matterId]);

  useEffect(() => {
    if (data?.status === "submitted") loadProposals();
  }, [data?.status, loadProposals]);

  const addCoverage = async (entry, match) => {
    setSending(true);
    try {
      await apiFetch(`/api/matters/${matterId}/insurance-policies`, {
        method: "POST",
        body: JSON.stringify({
          carrier_name: match ? match.name : entry.candidate,
          coverage_type: entry.coverage_type,
          is_client_policy: entry.is_client_policy,
        }),
      });
      showNotification?.(
        `${match ? match.name : entry.candidate} recorded as liability coverage.`,
        "success");
      await loadProposals();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSending(false);
  };

  const addLien = async (entry, lienType) => {
    setSending(true);
    try {
      await apiFetch(`/api/matters/${matterId}/liens`, {
        method: "POST",
        body: JSON.stringify({
          lienholder_name: entry.candidate,
          lien_type: lienType,
        }),
      });
      showNotification?.(`${entry.candidate} recorded as a lien.`, "success");
      await loadProposals();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSending(false);
  };

  const addProvider = async (p, match) => {
    setSending(true);
    try {
      if (match) {
        await apiFetch(`/api/matters/${matterId}/providers`, {
          method: "POST",
          body: JSON.stringify({ provider_id: match.id }),
        });
        showNotification?.(`${match.name} added to this matter.`, "success");
      } else {
        await apiFetch(`/api/matters/${matterId}/providers/custom`, {
          method: "POST",
          body: JSON.stringify({ name: p.candidate }),
        });
        showNotification?.(`${p.candidate} added to this matter.`, "success");
      }
      await loadProposals();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSending(false);
  };

  const send = async (to) => {
    setSending(true);
    try {
      const res = await apiFetch(`/api/matters/${matterId}/intake-sms`, {
        method: "POST",
        body: JSON.stringify(to ? { to_phone: to } : {}),
      });
      showNotification?.(`Intake form texted to ${res.to}.`, "success");
      setEditing(false);
      setPhone("");
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSending(false);
  };

  const emailForm = async () => {
    setSending(true);
    try {
      const res = await apiFetch(`/api/matters/${matterId}/intake-email`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      showNotification?.(`Intake form emailed to ${res.to}.`, "success");
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSending(false);
  };

  const copyLink = async (to) => {
    setSending(true);
    try {
      const res = await apiFetch(`/api/matters/${matterId}/intake-link`, {
        method: "POST",
        body: JSON.stringify(to ? { to_phone: to } : {}),
      });
      setLink(res);
      // Best effort. A denied clipboard permission must not look like
      // a failure to mint — the link is on screen either way.
      try { await navigator.clipboard.writeText(res.link); } catch (_e) { /* shown below */ }
      setEditing(false);
      setPhone("");
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSending(false);
  };

  if (!data) return null;

  const sent = data.status && data.status !== "pending";
  const done = data.status === "submitted";
  const stopped = data.status === "opted_out";

  const box = {
    background: "#fff", border: `1px solid ${C.warmGray}`,
    borderRadius: "8px", padding: "20px 24px", marginBottom: "20px",
  };
  const btn = (primary) => ({
    padding: "7px 14px", fontSize: "13px", cursor: "pointer",
    borderRadius: "5px",
    background: primary ? C.navy : "transparent",
    color: primary ? "#fff" : C.textMuted,
    border: primary ? "none" : `1px solid ${C.warmGray}`,
  });

  return React.createElement("div", { style: box },
    React.createElement("div", {
      style: { display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "4px" },
    },
      React.createElement("h3", {
        style: { margin: 0, fontSize: "15px", color: C.navy },
      }, "Client intake form"),
      React.createElement("span", { style: { flex: 1 } }),
      data.status && React.createElement("span", {
        style: { fontSize: "12px", color: C.textMuted },
      }, STATE[data.status] || data.status),
    ),

    React.createElement("p", {
      style: { margin: "0 0 14px", fontSize: "12px", color: C.textMuted },
    }, "Sent to the client as a link, by text or email. The message carries no case "
     + "details — everything they enter goes over the web."),

    data._error && React.createElement("p", {
      style: { fontSize: "13px", color: C.navy },
    }, data._error),

    sent && React.createElement("div", {
      style: { fontSize: "13px", color: C.navy, marginBottom: "12px", lineHeight: 1.7 },
    },
      React.createElement("div", null,
        `Sent to ${data.to_phone}`,
        data.send_count > 1 ? ` · ${data.send_count} times` : "",
        data.sent_at ? ` · ${fmt(data.sent_at)}` : ""),
      data.opened_at && React.createElement("div", {
        style: { color: C.textMuted },
      }, `Opened ${fmt(data.opened_at)}`),
      data.submitted_at && React.createElement("div", {
        style: { color: C.textMuted },
      }, `Completed ${fmt(data.submitted_at)}`),
    ),

    // What they said, once they have said it. Shown here rather than
    // only written through to the matter, because "what the client
    // actually told us" is worth being able to read back.
    done && data.answers && React.createElement("div", {
      style: {
        marginTop: "4px", marginBottom: "14px", paddingTop: "12px",
        borderTop: `1px solid ${C.warmGray}`,
      },
    },
      Object.entries(data.answers).map(([k, v]) =>
        React.createElement("div", {
          key: k,
          style: { display: "flex", gap: "12px", padding: "3px 0", fontSize: "13px" },
        },
          React.createElement("span", {
            style: { color: C.textMuted, minWidth: "150px" },
          }, k.replace(/_/g, " ")),
          React.createElement("span", { style: { color: C.navy } }, String(v)),
        )),
    ),

    // What the client told us about providers. Shown under the answers
    // because it is a follow-up to them, and every row needs a decision.
    proposals && proposals.proposals.length > 0 && React.createElement("div", {
      style: {
        marginTop: "4px", marginBottom: "14px", paddingTop: "12px",
        borderTop: `1px solid ${C.warmGray}`,
      },
    },
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textMuted, marginBottom: "10px" },
      }, "The client named these. Nothing has been added to the matter — "
       + "check each one."),

      ...proposals.proposals.map((p) => React.createElement("div", {
        key: p.candidate,
        style: {
          display: "flex", gap: "12px", alignItems: "baseline",
          padding: "7px 0", fontSize: "13px",
        },
      },
        React.createElement("span", {
          style: { color: C.navy, minWidth: "170px" },
        }, p.candidate),

        p.action === "linked"
          ? React.createElement("span", {
              style: { color: C.textMuted, fontSize: "12px" },
            }, "Already on this matter")
          : p.action === "link"
            ? React.createElement("span", { style: { display: "flex", gap: "8px", alignItems: "baseline" } },
                React.createElement("span", {
                  style: { color: C.textMuted, fontSize: "12px" },
                }, `${p.matches[0].name}${p.matches[0].city ? " · " + p.matches[0].city : ""}`),
                React.createElement("button", {
                  onClick: () => addProvider(p, p.matches[0]),
                  disabled: sending, style: btn(false),
                }, "Add"),
              )
            : p.action === "choose"
              ? React.createElement("span", { style: { display: "flex", flexWrap: "wrap", gap: "6px" } },
                  React.createElement("span", {
                    style: { color: C.textMuted, fontSize: "12px", marginRight: "2px" },
                  }, "Which one?"),
                  ...p.matches.map((m) => React.createElement("button", {
                    key: m.id,
                    onClick: () => addProvider(p, m),
                    disabled: sending, style: btn(false),
                  }, `${m.name}${m.city ? " · " + m.city : ""}`)),
                )
              : React.createElement("span", { style: { display: "flex", gap: "8px", alignItems: "baseline" } },
                  React.createElement("span", {
                    style: { color: C.textMuted, fontSize: "12px" },
                  }, "Not in the directory"),
                  React.createElement("button", {
                    onClick: () => addProvider(p, null),
                    disabled: sending, style: btn(false),
                  }, "Add as typed"),
                ),
      )),
    ),

    // Where the client contradicted the file. Above the proposals
    // because it changes how you read them.
    proposals?.mismatches?.length > 0 && React.createElement("div", {
      style: {
        marginBottom: "14px", paddingTop: "12px",
        borderTop: `1px solid ${C.warmGray}`,
      },
    },
      ...proposals.mismatches.map((m) => React.createElement("div", {
        key: m.field,
        style: { fontSize: "13px", padding: "7px 0", lineHeight: 1.7 },
      },
        React.createElement("div", { style: { color: C.navy } },
          `${m.field} — the client wrote "${m.client_said}", the file says `
          + `"${m.on_the_matter}".`),
        React.createElement("div", {
          style: { color: C.textMuted, fontSize: "12px" },
        }, m.note),
      )),
    ),

    // Insurance. Two answers, two destinations — the other driver's
    // carrier is coverage, the client's health plan is a lien. Kept
    // visibly apart so nobody reads a health plan as available money.
    proposals?.insurers && (proposals.insurers.at_fault.length > 0
                            || proposals.insurers.health.length > 0)
      && React.createElement("div", {
      style: {
        marginBottom: "14px", paddingTop: "12px",
        borderTop: `1px solid ${C.warmGray}`,
      },
    },
      ...proposals.insurers.at_fault.map((e) => React.createElement("div", {
        key: `af-${e.candidate}`,
        style: { display: "flex", gap: "12px", alignItems: "baseline",
                 padding: "7px 0", fontSize: "13px" },
      },
        React.createElement("span", {
          style: { color: C.navy, minWidth: "170px" },
        }, e.candidate),
        React.createElement("span", {
          style: { color: C.textMuted, fontSize: "12px" },
        }, "Other driver's carrier · liability"),
        e.action === "choose"
          ? React.createElement("span", { style: { display: "flex", flexWrap: "wrap", gap: "6px" } },
              ...e.matches.map((m) => React.createElement("button", {
                key: m.id, onClick: () => addCoverage(e, m),
                disabled: sending, style: btn(false),
              }, m.name)))
          : React.createElement("button", {
              onClick: () => addCoverage(e, e.matches[0] || null),
              disabled: sending, style: btn(false),
            }, e.action === "link" ? `Add ${e.matches[0].name}` : "Add as typed"),
      )),

      ...proposals.insurers.health.map((e) => React.createElement("div", {
        key: `h-${e.candidate}`,
        style: { display: "flex", gap: "12px", alignItems: "baseline",
                 padding: "7px 0", fontSize: "13px", flexWrap: "wrap" },
      },
        React.createElement("span", {
          style: { color: C.navy, minWidth: "170px" },
        }, e.candidate),
        e.type_certain
          ? React.createElement("span", { style: { display: "flex", gap: "8px", alignItems: "baseline" } },
              React.createElement("span", {
                style: { color: C.textMuted, fontSize: "12px" },
              }, `Health plan · ${e.lien_type}`),
              React.createElement("button", {
                onClick: () => addLien(e, e.lien_type),
                disabled: sending, style: btn(false),
              }, "Record lien"),
            )
          // Not a suggestion to accept — a question to answer. Whether
          // the plan is self-funded decides the reimbursement rules and
          // is not in the name.
          : React.createElement("span", { style: { display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "baseline" } },
              React.createElement("span", {
                style: { color: C.textMuted, fontSize: "12px" },
              }, "Health plan — self-funded?"),
              React.createElement("button", {
                onClick: () => addLien(e, "private_health"),
                disabled: sending, style: btn(false),
              }, "Private health"),
              React.createElement("button", {
                onClick: () => addLien(e, "erisa"),
                disabled: sending, style: btn(false),
              }, "ERISA"),
            ),
      )),
    ),

    link && React.createElement("div", {
      style: {
        marginBottom: "14px", padding: "12px 14px",
        border: `1px solid ${C.warmGray}`, borderRadius: "6px",
      },
    },
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textMuted, marginBottom: "6px" },
      }, "Copied to the clipboard. Read it out or paste it into an email — "
       + "it is shown once and cannot be retrieved again."),
      React.createElement("div", {
        style: {
          fontSize: "12px", color: C.navy, wordBreak: "break-all",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        },
      }, link.link),
      link.opted_out && React.createElement("div", {
        style: { fontSize: "12px", color: C.navy, marginTop: "8px" },
      }, "This client replied STOP. The link works, but do not text it "
       + "to them — give it to them another way."),
    ),

    stopped
      ? React.createElement("div", null,
          React.createElement("p", {
            style: { fontSize: "13px", color: C.navy, margin: "0 0 10px" },
          }, "This client has replied STOP. They cannot be texted again "
           + "until they ask to resume — call them instead."),
          React.createElement("div", { style: { display: "flex", gap: "8px" } },
            React.createElement("button", {
              onClick: emailForm, disabled: sending, style: btn(false),
            }, sending ? "Working…" : "Email it instead"),
            React.createElement("button", {
              onClick: () => copyLink(null), disabled: sending, style: btn(false),
            }, "Copy link"),
          ),
        )
      : editing
        ? React.createElement("div", { style: { display: "flex", gap: "8px" } },
            React.createElement("input", {
              value: phone, autoFocus: true, placeholder: "251-555-0143",
              onChange: (e) => setPhone(e.target.value),
              style: {
                flex: 1, padding: "7px 10px", fontSize: "13px",
                border: `1px solid ${C.warmGray}`, borderRadius: "5px",
              },
            }),
            React.createElement("button", {
              onClick: () => send(phone), disabled: sending || !phone.trim(),
              style: btn(true),
            }, sending ? "Sending…" : "Send"),
            React.createElement("button", {
              onClick: () => { setEditing(false); setPhone(""); },
              style: btn(false),
            }, "Cancel"),
          )
        : React.createElement("div", { style: { display: "flex", gap: "8px" } },
            React.createElement("button", {
              onClick: () => send(null), disabled: sending, style: btn(!done),
            }, sending ? "Sending…" : sent ? "Send again" : "Text the form"),
            React.createElement("button", {
              onClick: emailForm, disabled: sending, style: btn(false),
            }, "Email the form"),
            React.createElement("button", {
              onClick: () => copyLink(null), disabled: sending, style: btn(false),
            }, "Copy link"),
            React.createElement("button", {
              onClick: () => setEditing(true), style: btn(false),
            }, "Send to a different number"),
          ),
  );
}
